import React, { useState } from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #0a0a12;
  --panel: #101018;
  --surface: #16161f;
  --text: #f5f6fa;
  --dim: #a2a6b4;
  --faint: #6c7080;
  --accent: #8785f8;
  --danger: #ff6b6b;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 22px 80px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 22px;
  margin-bottom: 28px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: linear-gradient(150deg, #5b5fe9, #7c6bf2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.2px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  margin: 6px 0 4px;
`;

const Lede = styled.p`
  color: var(--dim);
  font-size: 16px;
  margin-bottom: 28px;

  strong {
    color: var(--text);
    font-weight: 600;
  }
`;

const Section = styled.h2`
  font-size: 19px;
  margin: 34px 0 10px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 16px;
  }

  ol,
  ul {
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  strong {
    color: var(--text);
    font-weight: 600;
  }

  a {
    color: var(--accent);
  }
`;

const Form = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 22px;
  margin-top: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: var(--text);
  font-weight: 600;
  margin: 14px 0 6px;

  &:first-child {
    margin-top: 0;
  }

  span {
    color: var(--faint);
    font-weight: 400;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--faint);
  }
`;

const Check = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 4px;
  color: var(--dim);
  font-size: 15px;
  cursor: pointer;

  input {
    margin-top: 4px;
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
    flex: none;
  }
`;

const Button = styled.button`
  width: 100%;
  height: 50px;
  margin-top: 18px;
  border: none;
  border-radius: 10px;
  background: var(--danger);
  color: #1a0808;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Error = styled.p`
  color: var(--danger);
  font-size: 14px;
  margin-top: 12px;
`;

const Success = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid var(--accent);
  border-radius: 10px;
  padding: 18px 20px;
  color: var(--dim);
  font-size: 15px;

  h3 {
    color: var(--text);
    font-size: 17px;
    margin: 0 0 8px;
  }

  a {
    color: var(--accent);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;

  td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    font-size: 15px;
    color: var(--dim);
    vertical-align: top;
  }

  td:first-child {
    color: var(--text);
    width: 46%;
  }
`;

const Footer = styled.footer`
  margin-top: 48px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 14px;

  a {
    color: var(--accent);
  }
`;

const Mark = () => (
  <svg viewBox="0 0 512 512" role="img" aria-label="AI Utility Hub">
    <g
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="34"
      strokeLinecap="butt"
      strokeLinejoin="miter"
      strokeMiterlimit="1.2"
    >
      <path d="M78 344 L128 184 L178 344" />
      <path d="M99 276 L157 276" />
      <path d="M222 184 L222 292 A42 42 0 0 0 306 292 L306 184" />
      <path d="M352 184 L352 344" />
      <path d="M430 184 L430 344" />
      <path d="M352 276 L430 276" />
    </g>
  </svg>
);

const SUPPORT = "hello@nimions.com";

const AiUtilityHubDelete = () => {
  const [email, setEmail] = useState("");
  const [uid, setUid] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // No backend on this site — the submission is simulated, and the mailto
  // below is the path that actually reaches us. Never ask for a password here:
  // this page cannot authenticate anyone, so a password field would only
  // train users to hand credentials to a look-alike page.
  const handleSubmit = () => {
    setError("");

    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter the email address on your account.");
      return;
    }
    if (!agreed) {
      setError("Please confirm you understand the deletion is permanent.");
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSent(true);
    }, 1200);
  };

  const mailto = `mailto:${SUPPORT}?subject=${encodeURIComponent(
    "AI Utility Hub — account deletion request"
  )}&body=${encodeURIComponent(
    `Please delete my AI Utility Hub account and all associated data.\n\n` +
      `Account email: ${email || "(your account email)"}\n` +
      `Account ID (optional): ${uid || "(from Profile in the app)"}\n`
  )}`;

  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>
            <Mark />
          </Badge>
          <Brand>
            AI Utility <span>Hub</span>
          </Brand>
        </Header>

        <Title>Delete your account</Title>
        <Lede>
          Request deletion of your <strong>AI Utility Hub</strong> account and
          the data associated with it. AI Utility Hub is published by{" "}
          <strong>Nimions</strong>.
        </Lede>

        <Body>
          <Section as="h2">How to request deletion</Section>
          <ol>
            <li>
              <strong>Clear the app on your device (optional).</strong> In the
              app, open <strong>Settings → Delete account</strong>. This removes
              your history, saved tools, credits and session from the device and
              signs you out. It does <strong>not</strong> remove the copy held on
              our servers, so continue to step 2.
            </li>
            <li>
              <strong>Submit the form below</strong>, or email{" "}
              <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> from the address on
              your account. Include your account email, and your account ID if
              you have it (Profile screen in the app).
            </li>
            <li>
              We confirm by email and delete your server-side data within{" "}
              <strong>30 days</strong>.
            </li>
          </ol>

          <Section as="h2">Request form</Section>
          {sent ? (
            <Success>
              <h3>Request received</h3>
              <p style={{ marginTop: 0 }}>
                Thanks — we’ll confirm by email and complete the deletion within
                30 days.
              </p>
              <p>
                To be certain it reaches us, please also send it directly:{" "}
                <a href={mailto}>email your request to {SUPPORT}</a>.
              </p>
            </Success>
          ) : (
            <Form>
              <Label htmlFor="email">Account email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <Label htmlFor="uid">
                Account ID <span>— optional, from Profile in the app</span>
              </Label>
              <Input
                id="uid"
                type="text"
                placeholder="e.g. uI4lZ3UUAsYrfRpIla0CWkQrBnU2"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
              />

              <Check htmlFor="agree">
                <input
                  id="agree"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  I understand this permanently deletes my account, my generation
                  history and any remaining credits, and that it cannot be undone.
                </span>
              </Check>

              {error && <Error>{error}</Error>}

              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? "Sending request…" : "Request account deletion"}
              </Button>
            </Form>
          )}

          <Section as="h2">What is deleted, and what is kept</Section>
          <Table>
            <tbody>
              <tr>
                <td>Your account and sign-in record</td>
                <td>Deleted within 30 days</td>
              </tr>
              <tr>
                <td>
                  Generation history — the text you entered and the results
                </td>
                <td>Deleted within 30 days</td>
              </tr>
              <tr>
                <td>Saved tools, credit balance, preferences</td>
                <td>Deleted within 30 days</td>
              </tr>
              <tr>
                <td>Everything stored on your device</td>
                <td>
                  Removed immediately by Settings → Delete account, or by
                  uninstalling the app
                </td>
              </tr>
              <tr>
                <td>Device record used to issue the one-time free credits</td>
                <td>
                  <strong>Kept.</strong> It holds no personal data — only that a
                  device already claimed its free allowance. Keeping it is what
                  stops the allowance being reissued
                </td>
              </tr>
              <tr>
                <td>Aggregate daily usage totals</td>
                <td>
                  <strong>Kept.</strong> Counts only, with no identifiers and
                  nothing that links back to you
                </td>
              </tr>
              <tr>
                <td>Purchase and billing records</td>
                <td>
                  <strong>Kept</strong> where tax or accounting law requires it,
                  and held by Google Play rather than by us
                </td>
              </tr>
            </tbody>
          </Table>

          <Section as="h2">Questions</Section>
          <p>
            Email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> or call{" "}
            <a href="tel:+8801764928732">+880 1764 928732</a>.
          </p>
        </Body>

        <Footer>
          © 2026 Nimions. This page covers the AI Utility Hub mobile application
          (package <strong>com.nimions.utilityhub</strong>). See also our{" "}
          <a href="/privacy/ai-utility-hub">Privacy Policy</a> and{" "}
          <a href="/terms/ai-utility-hub">Terms &amp; Conditions</a>.
        </Footer>
      </Wrap>
    </Page>
  );
};

export default AiUtilityHubDelete;
